import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import React, { useEffect, useState, useRef } from 'react';
import ApiClient from "../../utils/ApiClient";
import { rackModalOpen } from "./SfpRackModal";

export default function RAPackingListInput({
                                               packingListId,
                                               packingListData,
                                               poNumber,
                                               onScannedDataUpdate
                                           }) {
    const [debounceTimer, setDebounceTimer] = useState(null);
    const [productId, setProductId] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [scanResponse, setScanResponse] = useState(null);
    const [responseMessage, setResponseMessage] = useState('');
    const inputRef = useRef(null);

    const handleProductIdChange = (e) => {
        const value = e.target.value;
        setProductId(value);

        // Only clear error and response if new input is being entered
        if (value !== '') {
            setError('');
            setScanResponse(null);
            setResponseMessage('');
        }

        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }

        const timer = setTimeout(() => {
            processProductId(value);
        }, 1000);

        setDebounceTimer(timer);
    };

    const processProductId = async (value) => {
        if (!value || !packingListId || isProcessing) return;

        setIsProcessing(true);
        const successTone = new Audio('/sounds/success-tone.mp3');
        const errorTone = new Audio('/sounds/error-tone.mp3');

        try {
            if (value.startsWith("LPN")) {
                throw new Error("Invalid product ID format");
            }

            const response = await ApiClient.post("/api/receiveinventory/scan", {
                productId: value,
                packingListId: packingListId,
                poNumber: poNumber
            });

            console.log('Scan Response:', response.data);

            const responseCode = Number(response.data);
            if (responseCode === 1) {
                successTone.play();
                setResponseMessage('');
                setScanResponse(1);
                // Only clear product ID and update scan data on success
                setProductId('');
                if (onScannedDataUpdate) {
                    await onScannedDataUpdate();
                }
            } else {
                errorTone.play();
                if (responseCode === 0) {
                    setResponseMessage("ASIN not found with GTIN/UPC/EAN");
                    setScanResponse(0);
                } else if (responseCode === 2) {
                    setResponseMessage("Product ID or packing list ID is null");
                    setScanResponse(2);
                } else if (responseCode === 3) {
                    setResponseMessage("Item is not on this packing list — not received");
                    setScanResponse(3);
                }
                // Clear product ID but don't update scan data for errors
                setProductId('');
            }

            setError('');

        } catch (error) {
            console.error('Scan error:', error);
            setError(error.message || 'Failed to process scan');
            setScanResponse(null);
            setResponseMessage('');
            errorTone.play();
        } finally {
            setIsProcessing(false);
        }
    };

    useEffect(() => {
        if (!rackModalOpen()) inputRef.current?.focus();
        return () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
        };
    }, [debounceTimer]);

    useEffect(() => {
        if (productId === '' && !rackModalOpen()) {
            inputRef.current?.focus();
        }
    }, [productId]);

    return (
        <div className="relative flex flex-col w-[400px]">
            <label htmlFor="product-id" className="text-sm font-medium text-gray-900">
                UPC, EAN, ASIN, SKU, or FNSKU
            </label>
            <div className="relative mt-1 rounded-md shadow-sm">
                <input
                    ref={inputRef}
                    data-receive-scan="1"
                    id="product-id"
                    type="text"
                    placeholder="Scan Product UPC/EAN/GTIN"
                    value={productId}
                    onChange={handleProductIdChange}
                    disabled={isProcessing || !packingListId}
                    className={`block w-full h-[38px] rounded-md border-0 py-1.5 pr-10 
                        ${error
                        ? 'text-red-900 ring-red-300 placeholder-red-300 focus:ring-red-500'
                        : 'text-gray-900 ring-gray-300 placeholder-gray-400 focus:ring-indigo-500'
                    } 
                        ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6
                        ${isProcessing ? 'bg-gray-100' : ''}
                        ${!packingListId ? 'cursor-not-allowed bg-gray-100' : ''}
                    `}
                    aria-invalid={!!error}
                    aria-describedby={error ? "input-error" : undefined}
                />
                {error && (
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div>
                )}
            </div>
            {!packingListId && (
                <p className="mt-2 text-sm text-gray-500">
                    Please select a packing list to begin scanning
                </p>
            )}
            {error && (
                <p className="mt-2 text-sm text-red-600" id="input-error">
                    {error}
                </p>
            )}
            {responseMessage !== '' && (
                <p className="mt-2 text-sm text-amber-600" id="response-message">
                    {responseMessage}
                </p>
            )}
            {isProcessing && (
                <div className="absolute right-0 top-0 h-full flex items-center pr-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                </div>
            )}
        </div>
    );
}